import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import {
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  X
} from 'lucide-react';
import { useInsightsQuery, useRecurringTransactionsQuery, useStatsQuery, useSummaryQuery } from '../hooks/useQueries';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { generateInsights } from '../utils/insightGenerator';

// Donut chart SVG component
function DonutChart({ needs, wants, savings, totalSpent, formatAmount }) {
  const total = needs + wants;
  if (total === 0) return (
    <div className="text-center py-6">
      <span className="text-3xl block mb-2">📊</span>
      <p className="text-sm font-semibold text-surface-900">No spending yet</p>
      <p className="text-xs text-surface-400 mt-0.5">Add expenses to see your spending split</p>
    </div>
  );

  const needsPct = Math.round((needs / (needs + wants)) * 100);
  const wantsPct = 100 - needsPct;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-[120px] h-[120px] flex-shrink-0">
        <svg viewBox="0 0 36 36" className="w-[120px] h-[120px]" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3b82f6" strokeWidth="3.5"
            strokeDasharray={`${needsPct} ${100 - needsPct}`} strokeDashoffset="0" strokeLinecap="round" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#8b5cf6" strokeWidth="3.5"
            strokeDasharray={`${wantsPct} ${100 - wantsPct}`} strokeDashoffset={`${-needsPct}`} strokeLinecap="round" />
        </svg>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
          <div className="text-sm font-extrabold text-surface-900">{formatAmount(totalSpent)}</div>
          <div className="text-[9px] text-surface-400">Total Spent</div>
        </div>
      </div>
      <div className="flex-1 space-y-2.5">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-surface-900">Needs</div>
            <div className="text-[10px] text-surface-400">{formatAmount(needs)}</div>
          </div>
          <span className="text-sm font-bold text-blue-500">{needsPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm bg-purple-500 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-surface-900">Wants</div>
            <div className="text-[10px] text-surface-400">{formatAmount(wants)}</div>
          </div>
          <span className="text-sm font-bold text-purple-500">{wantsPct}%</span>
        </div>
        {savings > 0 && (
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-surface-900">Savings</div>
              <div className="text-[10px] text-surface-400">{formatAmount(savings)}</div>
            </div>
            <span className="text-sm font-bold text-emerald-500">{Math.round((savings / (needs + wants + savings)) * 100)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Progress bar with target marker
function TargetBar({ value, target, color }) {
  const pct = Math.min(Math.max(value, 0), 100);
  return (
    <div className="relative h-2 bg-surface-200 rounded-full" style={{ overflow: 'visible' }}>
      <div className="absolute h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      <div className="absolute -top-0.5 w-0.5 h-3 bg-surface-800 rounded-sm" style={{ left: `${Math.min(target, 100)}%` }} />
    </div>
  );
}

// Category bar for breakdown tab
function CategoryBar({ name, emoji, amount, percentage, color, formatAmount: fmt }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-surface-600">{emoji} {name}</span>
        <span className="text-xs font-semibold text-surface-900">
          {fmt(amount)} <span className="text-surface-400 font-normal text-[10px]">({percentage}%)</span>
        </span>
      </div>
      <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${percentage}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

const EMOJI_MAP = {
  warning: '⚠️',
  tip: '💡',
  success: '✅'
};

const BG_MAP = {
  warning: 'bg-amber-50 border-amber-200',
  tip: 'bg-blue-50 border-blue-200',
  success: 'bg-income-50 border-income-200'
};

const ICON_BG_MAP = {
  warning: 'bg-amber-100',
  tip: 'bg-blue-100',
  success: 'bg-income-100'
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const TABS = ['Overview', 'Budget Rules', 'Breakdown'];

export default function InsightsPage() {
  const { formatAmount, config } = useConfig();
  const { hasEncryptionKey } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showCategoryInfo, setShowCategoryInfo] = useState(false);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (isCurrentMonth) return;
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonth(m => m + 1);
    }
  };

  const monthLabel = `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`;

  const { data, isLoading, isError, refetch, isFetching } = useInsightsQuery({ month: selectedMonth, year: selectedYear });

  const { data: summaryData } = useSummaryQuery({}, { enabled: hasEncryptionKey });
  const { data: statsData } = useStatsQuery(
    { type: 'expense', period: 'thisMonth' },
    { enabled: hasEncryptionKey }
  );
  const { data: recurringData = {} } = useRecurringTransactionsQuery({}, {
    enabled: hasEncryptionKey
  });
  const recurringTransactions = recurringData.recurringTransactions || [];

  const dayOfMonth = now.getDate();
  const transactionCount = data?.transactionCount ?? 0;
  const showEarlyMonthGuard = isCurrentMonth && (dayOfMonth < 15 || transactionCount < 10);

  const allInsights = useMemo(() => {
    return generateInsights({
      statsData,
      summary: summaryData,
      recurringTransactions,
      formatAmount
    });
  }, [statsData, summaryData, recurringTransactions, formatAmount]);

  const { breakdown, percentages, rules, bestMatch, categories, changes } = data || {};

  const activeRule = selectedRule || bestMatch?.id;
  const activeRuleData = rules?.find(r => r.id === activeRule);

  const hasNoData = !isLoading && !isError && (!breakdown || (breakdown.income === 0 && breakdown.needs === 0 && breakdown.wants === 0));

  const totalSpent = (breakdown?.needs || 0) + (breakdown?.wants || 0);
  const savingsAmount = breakdown?.savings || 0;
  const savingsPct = breakdown?.income > 0 ? Math.round((savingsAmount / breakdown.income) * 100) : 0;

  return (
    <div className="space-y-3 pb-20 xl:pb-4">
      {/* Header - Mobile */}
      <StickyPageHeader className="xl:hidden flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-surface-900">Spending Insights</h1>
          <p className="text-[11px] text-surface-400">Track your spending patterns</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="w-9 h-9 rounded-xl flex items-center justify-center border bg-white border-surface-200 text-surface-600 disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </StickyPageHeader>

      {/* Header - Desktop */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Spending Insights</h1>
          <p className="page-subtitle">Track your spending patterns</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 text-sm text-surface-600 hover:text-surface-900 hover:bg-surface-100 rounded-xl transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={goToPrevMonth}
          className="w-7 h-7 rounded-lg flex items-center justify-center border border-surface-200 bg-white hover:bg-surface-50 active:scale-95 transition-all"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-surface-600" />
        </button>
        <span className="text-sm font-bold text-surface-900">{monthLabel}</span>
        <button
          onClick={goToNextMonth}
          disabled={isCurrentMonth}
          className="w-7 h-7 rounded-lg flex items-center justify-center border border-surface-200 bg-white hover:bg-surface-50 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-3.5 h-3.5 text-surface-600" />
        </button>
      </div>

      {/* Summary Cards */}
      {!isLoading && !isError && !hasNoData && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white rounded-xl border border-surface-100 px-3 py-2.5 text-center">
            <p className="text-[10px] text-surface-400 mb-0.5">Income</p>
            <p className="text-xs font-bold" style={{ color: config.incomeColor }}>{formatAmount(breakdown?.income || 0)}</p>
          </div>
          <div className="bg-white rounded-xl border border-surface-100 px-3 py-2.5 text-center">
            <p className="text-[10px] text-surface-400 mb-0.5">Spent</p>
            <p className="text-xs font-bold" style={{ color: config.expenseColor }}>{formatAmount(totalSpent)}</p>
          </div>
          <div className="bg-white rounded-xl border border-surface-100 px-3 py-2.5 text-center">
            <p className="text-[10px] text-surface-400 mb-0.5">Saved</p>
            <p className={`text-xs font-bold ${savingsAmount >= 0 ? 'text-surface-900' : 'text-expense-600'}`}>
              {savingsAmount < 0 ? `-${formatAmount(Math.abs(savingsAmount))}` : formatAmount(savingsAmount)}
            </p>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-4">
          <div className="h-48 bg-surface-100 rounded-xl animate-pulse" />
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-surface-100 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="text-center py-12 bg-white rounded-2xl border border-surface-200">
          <span className="text-4xl block mb-3">😵</span>
          <h2 className="text-base font-semibold text-surface-900 mb-1">Unable to Load</h2>
          <p className="text-sm text-surface-500 mb-4">Please try again.</p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      )}

      {/* Empty state */}
      {hasNoData && (
        <div className="text-center py-12 bg-white rounded-2xl border border-surface-200">
          <span className="text-4xl block mb-4">🐷</span>
          <h2 className="text-base font-semibold text-surface-900 mb-2">No Data for {monthLabel}</h2>
          <p className="text-sm text-surface-500 max-w-xs mx-auto px-4">
            No income or expenses found for this month.
          </p>
          <p className="text-xs text-surface-400 mt-1 px-4">
            Use the arrows above to navigate to a different month.
          </p>
        </div>
      )}

      {/* Early month guard */}
      {!isLoading && !isError && !hasNoData && showEarlyMonthGuard && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">⚠️</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">Not enough data yet</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Insights are available after 15 days into the month or at least 10 transactions.
                Currently: day {dayOfMonth}, {transactionCount} transaction{transactionCount !== 1 ? 's' : ''}.
              </p>
              <button
                onClick={goToPrevMonth}
                className="text-xs font-medium mt-2 inline-flex items-center gap-0.5 text-amber-700 hover:text-amber-900"
              >
                <ChevronLeft className="w-3 h-3" />
                View previous month
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content with tabs */}
      {!isLoading && !isError && !hasNoData && (
      <>
        {/* Tab Bar */}
        <div className="grid grid-cols-3 gap-1 p-1 bg-surface-100 rounded-xl">
          {TABS.map((tab, idx) => (
            <button
              key={tab}
              onClick={() => setActiveTab(idx)}
              className={`py-2 px-1 text-xs font-semibold rounded-lg transition-all ${
                activeTab === idx
                  ? 'bg-white text-surface-900 shadow-sm'
                  : 'text-surface-400 hover:text-surface-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ========== TAB 0: OVERVIEW ========== */}
        {activeTab === 0 && (
          <div className="space-y-3">
            {/* Best Match Card */}
            {bestMatch && (
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--accent-bg)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    {bestMatch.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] opacity-70" style={{ color: 'var(--accent-text)' }}>Your spending best matches</p>
                    <p className="text-[15px] font-bold" style={{ color: 'var(--accent-text)' }}>{bestMatch.name}</p>
                    <p className="text-[10px] opacity-60 mt-0.5" style={{ color: 'var(--accent-text)' }}>{bestMatch.description}</p>
                  </div>
                  <div
                    className="px-3.5 py-1.5 rounded-full text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: 'var(--accent-text)', color: 'var(--accent-bg)' }}
                  >
                    {bestMatch.matchScore}%
                  </div>
                </div>
              </div>
            )}

            {/* Donut Chart - Spending Split */}
            <div className="bg-white rounded-2xl border border-surface-200 p-4">
              <h2 className="text-sm font-bold text-surface-900 mb-3">Spending Split</h2>
              <DonutChart
                needs={breakdown?.needs || 0}
                wants={breakdown?.wants || 0}
                savings={savingsAmount > 0 ? savingsAmount : 0}
                totalSpent={totalSpent}
                formatAmount={formatAmount}
              />
            </div>

            {/* Savings Status */}
            {savingsAmount < 0 ? (
              <div className="flex items-center gap-3 p-3.5 bg-expense-50 border border-expense-200 rounded-xl">
                <div className="w-9 h-9 rounded-lg bg-expense-100 flex items-center justify-center text-lg flex-shrink-0">🚨</div>
                <div>
                  <p className="text-sm font-semibold text-expense-700">Spending more than earning</p>
                  <p className="text-xs text-expense-600 mt-0.5">
                    You've spent {formatAmount(Math.abs(savingsAmount))} more than your income.
                  </p>
                </div>
              </div>
            ) : savingsPct >= 20 ? (
              <div className="flex items-center gap-3 p-3.5 bg-income-50 border border-income-200 rounded-xl">
                <div className="w-9 h-9 rounded-lg bg-income-100 flex items-center justify-center text-lg flex-shrink-0">✅</div>
                <div>
                  <p className="text-sm font-semibold text-income-700">Great savings rate!</p>
                  <p className="text-xs text-income-600 mt-0.5">
                    You're saving {savingsPct}% of your income. Keep it up!
                  </p>
                </div>
              </div>
            ) : savingsPct > 0 ? (
              <div className="flex items-center gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-lg flex-shrink-0">💰</div>
                <div>
                  <p className="text-sm font-semibold text-amber-700">Room to save more</p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    You're saving {savingsPct}% — aim for 20% or more.
                  </p>
                </div>
              </div>
            ) : null}

            {/* Recommendations (full-width stacked) */}
            {allInsights.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-surface-900 mb-2">Recommendations</h2>
                <div className="space-y-2.5">
                  {allInsights.slice(0, 4).map((insight, idx) => {
                    const emoji = EMOJI_MAP[insight.type] || '💡';
                    const bg = BG_MAP[insight.type] || BG_MAP.tip;
                    const iconBg = ICON_BG_MAP[insight.type] || ICON_BG_MAP.tip;

                    return (
                      <div key={idx} className={`rounded-xl p-3.5 border ${bg}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center text-lg flex-shrink-0`}>
                            {emoji}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-bold text-surface-900 leading-tight">{insight.title}</p>
                            <p className="text-xs text-surface-600 mt-0.5 leading-relaxed">{insight.description}</p>
                            {insight.action && (
                              <button
                                onClick={() => navigate(insight.action.path)}
                                className="text-xs font-semibold mt-2 inline-flex items-center gap-0.5"
                                style={{ color: 'var(--accent-bg)' }}
                              >
                                {insight.action.label} →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== TAB 1: BUDGET RULES ========== */}
        {activeTab === 1 && (
          <div className="space-y-3">
            {rules?.map((rule) => {
              const isActive = activeRule === rule.id;
              const isBestMatch = bestMatch?.id === rule.id;

              return (
                <div
                  key={rule.id}
                  className={`bg-white rounded-xl border-2 transition-all cursor-pointer ${
                    isActive
                      ? isBestMatch ? 'border-primary-500 bg-income-50/30' : 'border-primary-500 bg-primary-50/30'
                      : 'border-surface-200 hover:border-surface-300'
                  }`}
                  onClick={() => setSelectedRule(rule.id === selectedRule ? null : rule.id)}
                >
                  <div className="p-4">
                    {/* Rule header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0 ${isActive ? 'bg-income-100' : 'bg-surface-100'}`}>
                        {rule.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-bold text-surface-900 truncate">{rule.name}</h3>
                          {isBestMatch ? (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-income-100 text-income-700 flex-shrink-0">Best Match</span>
                          ) : (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-100 text-surface-500 flex-shrink-0">{rule.matchScore}%</span>
                          )}
                        </div>
                        <p className="text-[11px] text-surface-500 mt-0.5">{rule.description}</p>
                      </div>
                    </div>

                    {/* Expanded: bars with target markers */}
                    {isActive && (
                      <>
                        {/* Needs */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-semibold text-surface-600">🏠 Needs</span>
                            <span className="text-[11px] font-semibold text-blue-500">
                              {percentages?.needs || 0}% <span className="text-surface-400 font-normal">/ {rule.target.needs}%</span>
                            </span>
                          </div>
                          <TargetBar value={percentages?.needs || 0} target={rule.target.needs} color="#3b82f6" />
                        </div>

                        {/* Wants */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-semibold text-surface-600">🛍️ Wants</span>
                            <span className="text-[11px] font-semibold text-purple-500">
                              {percentages?.wants || 0}% <span className="text-surface-400 font-normal">/ {rule.target.wants}%</span>
                            </span>
                          </div>
                          <TargetBar value={percentages?.wants || 0} target={rule.target.wants} color="#8b5cf6" />
                        </div>

                        {/* Savings */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-semibold text-surface-600">🐷 Savings</span>
                            <span className="text-[11px] font-semibold text-emerald-500">
                              {Math.max(0, percentages?.savings || 0)}% <span className="text-surface-400 font-normal">/ {rule.target.savings}%</span>
                            </span>
                          </div>
                          <TargetBar value={Math.max(0, percentages?.savings || 0)} target={rule.target.savings} color="#10b981" />
                        </div>

                        {/* Your vs Target */}
                        {rule.deviation && (
                          <div className="pt-3 border-t border-surface-200">
                            <p className="text-[11px] font-semibold text-surface-600 mb-1.5">Your vs Target</p>
                            {rule.deviation.needs !== 0 && (
                              <p className={`text-[11px] mb-0.5 ${rule.deviation.needs > 0 ? 'text-expense-600' : 'text-income-600'}`}>
                                • Needs: {Math.abs(rule.deviation.needs)}% {rule.deviation.needs > 0 ? 'over' : 'under'} target
                              </p>
                            )}
                            {rule.deviation.wants !== 0 && (
                              <p className={`text-[11px] mb-0.5 ${rule.deviation.wants > 0 ? 'text-expense-600' : 'text-income-600'}`}>
                                • Wants: {Math.abs(rule.deviation.wants)}% {rule.deviation.wants > 0 ? 'over' : 'under'} target
                              </p>
                            )}
                            {rule.deviation.savings !== 0 && (
                              <p className={`text-[11px] ${rule.deviation.savings < 0 ? 'text-expense-600' : 'text-income-600'}`}>
                                • Savings: {Math.abs(rule.deviation.savings)}% {rule.deviation.savings < 0 ? 'below' : 'above'} target {rule.deviation.savings > 0 ? '✓' : ''}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Collapsed: simple bars */}
                    {!isActive && (
                      <div className="space-y-1.5">
                        {[
                          { label: 'Needs', value: rule.target.needs, color: '#3b82f6' },
                          { label: 'Wants', value: rule.target.wants, color: '#8b5cf6' },
                          { label: 'Savings', value: rule.target.savings, color: '#10b981' }
                        ].map(item => (
                          <div key={item.label} className="flex items-center gap-2">
                            <span className="text-[10px] text-surface-400 w-12">{item.label}</span>
                            <div className="flex-1 h-1.5 bg-surface-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${item.value}%`, backgroundColor: item.color }} />
                            </div>
                            <span className="text-[10px] font-medium text-surface-500 w-7">{item.value}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* How it works */}
            <div className="bg-surface-50 rounded-xl p-4 border border-surface-200">
              <h3 className="text-sm font-bold text-surface-900 mb-2">How budget rules work</h3>
              <p className="text-xs text-surface-600 leading-relaxed">
                Budget rules help you allocate your income effectively. The most popular is the <strong>50-30-20 rule</strong>:
                50% for needs (essentials), 30% for wants (lifestyle), and 20% for savings.
              </p>
              <p className="text-xs text-surface-500 mt-2 italic">
                Categories are auto-classified as needs or wants based on their names.
              </p>
            </div>
          </div>
        )}

        {/* ========== TAB 2: BREAKDOWN ========== */}
        {activeTab === 2 && (
          <div className="space-y-3">
            {/* Top Needs */}
            {categories?.needs?.length > 0 && (
              <div className="bg-white rounded-xl border border-surface-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🏠</span>
                    <h3 className="text-sm font-bold text-surface-900">Top Needs</h3>
                  </div>
                  <span className="text-xs text-surface-400">{formatAmount(breakdown?.needs || 0)}</span>
                </div>
                {categories.needs.map((cat, idx) => {
                  const pct = (breakdown?.needs || 0) > 0 ? Math.round((cat.amount / breakdown.needs) * 100) : 0;
                  const colors = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'];
                  return (
                    <CategoryBar
                      key={idx}
                      name={cat.name}
                      emoji={cat.emoji || ''}
                      amount={cat.amount}
                      percentage={pct}
                      color={colors[idx] || colors[colors.length - 1]}
                      formatAmount={formatAmount}
                    />
                  );
                })}
              </div>
            )}

            {/* Top Wants */}
            {categories?.wants?.length > 0 && (
              <div className="bg-white rounded-xl border border-surface-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🛍️</span>
                    <h3 className="text-sm font-bold text-surface-900">Top Wants</h3>
                  </div>
                  <span className="text-xs text-surface-400">{formatAmount(breakdown?.wants || 0)}</span>
                </div>
                {categories.wants.map((cat, idx) => {
                  const pct = (breakdown?.wants || 0) > 0 ? Math.round((cat.amount / breakdown.wants) * 100) : 0;
                  const colors = ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];
                  return (
                    <CategoryBar
                      key={idx}
                      name={cat.name}
                      emoji={cat.emoji || ''}
                      amount={cat.amount}
                      percentage={pct}
                      color={colors[idx] || colors[colors.length - 1]}
                      formatAmount={formatAmount}
                    />
                  );
                })}
              </div>
            )}

            {/* Savings Card */}
            {savingsAmount > 0 && (
              <div className="rounded-xl p-4 border border-income-200" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-income-200 flex items-center justify-center text-xl flex-shrink-0">🐷</div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-income-700">Savings This Month</p>
                    <p className="text-lg font-extrabold text-income-600 mt-0.5">{formatAmount(savingsAmount)}</p>
                    <p className="text-[10px] text-income-600 mt-0.5">{savingsPct}% of income saved</p>
                  </div>
                </div>
              </div>
            )}

            {/* Category info */}
            <button
              onClick={() => setShowCategoryInfo(prev => !prev)}
              className="text-xs text-surface-400 hover:text-surface-600 transition-colors"
            >
              How are categories classified? →
            </button>
            {showCategoryInfo && (
              <div className="p-3 bg-surface-50 rounded-xl border border-surface-200 text-xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-surface-700">How we classify your spending</span>
                  <button onClick={() => setShowCategoryInfo(false)} className="text-surface-400 hover:text-surface-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="space-y-2 text-surface-600">
                  <div><span className="font-medium text-blue-600">🏠 Needs:</span> Rent, Groceries, Utilities, Insurance, Transport, EMI, Education, Medical</div>
                  <div><span className="font-medium text-purple-600">🛍️ Wants:</span> Dining, Entertainment, Shopping, Travel, Subscriptions, Gym, Beauty</div>
                  <div><span className="font-medium text-income-600">🐷 Savings:</span> Income minus all expenses</div>
                </div>
                <p className="mt-2 text-surface-500 italic">Categories are auto-classified based on their names.</p>
              </div>
            )}
          </div>
        )}
      </>
      )}
    </div>
  );
}
